import type { MetaDataSchema } from '../mdf/metadataSchema';
import { DEFAULT_MDF_ID } from '../mdf/mdfDefaults';
import type { Alternative, MdfFieldEntity } from '../mdf/fieldDefinition';
import type { LayoutSettings } from '../dto/mdfModels';
import { FieldType } from '../mdf/fieldDefinition';
import { ViewType } from '../dto/mdfModels';
import type { TimedCoordinate } from '../dto/GeoLocation';
import capitalize from 'lodash/capitalize';
import isEmpty from 'lodash/isEmpty';
import isEqual from 'lodash/isEqual';
import split from 'lodash/split';
import startsWith from 'lodash/startsWith';
import { rangeQueryPatternRegex, relativeTimeDetectPatternRegex, dateRangePatternRegex } from '../util/constants';
import { isFullMonth, isFullYear, isSameDay, isValidDate } from '../util/date';
import type { VueSet } from '../util/general';
import { isValidGUID, isArrayOfString } from '../util/general';
import type { SearchCategory, FilterType, RelativeDateFilter } from '../dto/searchModels';
import { StaticFilterKeywords, hasSearchCategoryOnly, isSearchCategory, SearchDateOption, filterTypeForKey, METADATA_FIELD_FILTER_PREFIX } from '../dto/searchModels';
import { getLabelForSearchCategory } from '../util/item';
import type { ParsedFilter, SeparatedFilters } from './filter-util';
import { getFiltersFromSearchString, isQuotedText, parseBool, removeFiltersFromString, TREE_CHOICE_SEPARATOR } from './filter-util';
import type { UserInfoDto } from '../dto/userDto';
import { getUserDisplayName } from '../dto/userDto';
import type { TimedMdfUsageRule } from '../dto/timedMdfUsage';
import type { OptionListDto } from '../dto/optionListDto';
import { getChoiceAlternatives } from '../dto/optionListDto';
import { DateHelper } from '../util/DateUtil';
import { assert, invariant } from '../util/invariant';
import type { Logger } from '../types/logger';
import type { KeldaConfigDto } from '@mimir/common/dto/keldaConfigDto';
import { formatSemanticRelativeDate, parseRelativeTimeString, generateRelativeTimeString } from '../util/relativeDateUtils';
import { isDateRangeSyntax } from '../util/dateFilterUtils';

/**
 * Used to append more information to a filter. This is used client side to convey
 * extra information in a searchString, that should be ignored serverside.
 */
export const filterDelimiter = '|';

export type DelimiterSupport = Partial<{ [V in FilterType]: boolean; }>;

export type ArchivedFilterType = 'yes' | 'no' | 'failed';

type FilterViewValue = { value: string, isSticky: boolean };

/**
 * We want to control which Filters have delimiter support. This is used
 * serverside to determine whether it will attempt to strip values away.
 */
export const delimiterSupport: DelimiterSupport = {
  'filter:profileId': true,
  'filter:folderId': true,
  'filter:title': true,
  'filter:description': true,
  'filter:transcript': true,
  'filter:folder': true,
  'filter:sourceBucket': true,
  'filter:formId': true,
  'filter:itemState': true,
  'filter:itemType': true,
  'filter:label': true,
  'filter:person': true
};

export const extraInfoSticky = 'sticky';

/**
 * Most filters deserialize into a search string, but we also support filters
 * that serialize down to one or more query parameters. For now folders are implemented this way.
 */
export enum SerializeMode {
  String,
  Parameters
}

/**
 * Defines all the default filters.
 *
 * The keys are a subset of FilterType.
 * Only filters types for which the keys are predefined are defined
 * here, so this type does not cover filters like metadataField, where
 * the key depends on the fieldId.
 */
export type DefaultFilters = {
  'filter:itemType': TypeFilter,
  'filter:person': StringFilter,
  'filter:transcript': StringFilter,
  'filter:timedTranslatedTranscript': StringFilter,
  'filter:label': StringFilter,
  'filter:metadata': StringFilter,
  'filter:events': StringFilter,
  'filter:text': StringFilter,
  'filter:formId': StringFilter,
  'filter:createdDate': DateRangeFilter,
  'filter:mediaCreatedOn': DateRangeFilter,
  'filter:scheduledRecording.startTimeMs': DateRangeFilter,
  'filter:modifiedOn': DateRangeFilter,
  'filter:purgeDate': DateRangeFilter,
  'filter:geoLocation': GeoShapeRangeFilter,
  'filter:profileId': ProfileIdFilter,
  'filter:itemState': StringFilter,
  'filter:folder': StringFilter,
  'filter:fileName': StringFilter,
  'filter:archived': ArchivedFilter,
  'filter:orphan': BooleanFilter,
  'filter:sourceBucket': StringFilter,
  'filter:folderId': FolderIdFilter,
  'filter:itemId': ItemIdFilter,
  'filter:owner': UserFilter,
  'filter:vclip': BooleanFilter,
  'filter:rule': UsageRuleKeyFilter,
  'filter:ruleContext': UsageRuleContextFilter,
  'filter:ruleValidity': UsageRuleValidityFilter,
  'filter:missing': StringFilter,
  'filter:notEmpty': StringFilter,
  'filter:mediaDuration': DurationRangeFilter,
  'filter:title': StringFilter,
  'filter:description': StringFilter,
  'filter:highresDeletionState': StringFilter,
  'filter:highresDeletion': StringFilter,
  'filter:aspectRatio': StringFilter,
  'filter:hasRelations': BooleanFilter,
  'filter:onlyNonCurrentVersions': BooleanFilter,
  'filter:ingestedByKelda': KeldaConfigFilter,
  'filter:uploadStatus': StringFilter,
  'filter:pinned': StringFilter,
};

export function isDefaultFilterType(type: FilterType): type is keyof DefaultFilters {
  if (type === 'filter:metadataField' || type === 'filter:eventsForm' || type === 'filter:eventsField') {
    return false;
  }
  // This assignment will make sure the compiler checks that
  // the remaining subset of FilterType is compatible with DefaultFilters.
  const _t: keyof DefaultFilters = type;
  return !!_t;
}

/**
 * Functions as a wrapper class around default and custom filters.
 */
export class Filters {

  private geoShapeFilter: GeoShapeRangeFilter;
  private profileIdFilter = new ProfileIdFilter();
  private typeFilter = new TypeFilter();
  private folderIdFilter = new FolderIdFilter();
  private itemIdFilter = new ItemIdFilter();

  public dateFilter: DateRangeFilter;
  public customFilters: Record<string, Filter<any> | undefined> = {};
  public defaultFilters: DefaultFilters;

  // we pass the appState.dateHelper here which in turn we pass down to the date range filters' constructors
  // so that the client and the date filters share the same instance of DateHelper class. This way we don't
  // need to set the proper timezone in the date filters explicitly
  constructor(private logger: Logger, private dateHelper: DateHelper) {
    this.geoShapeFilter = new GeoShapeRangeFilter(logger, 'geoLocation');
    this.dateFilter = new DateRangeFilter({
      logger,
      key: 'mediaCreatedOn',
      dateHelper: this.dateHelper,
      alias: 'Media Created',
      readableKey: 'mediaCreatedOn'
    });
    this.defaultFilters = {
      'filter:itemType': this.typeFilter,
      'filter:title': new StringFilter('default_title', 'title', '#21859C', 'titles', false),
      'filter:person': new StringFilter('person', 'person', '#424874', 'people', false, 'Search for people'),
      'filter:description': new StringFilter('default_description', 'notes', '#d63a3b', 'descriptions', false),
      'filter:transcript': new StringFilter('transcript', 'closed_caption', '#F05D5E', 'transcripts', false),
      'filter:timedTranslatedTranscript': new StringFilter('timedTranslatedTranscript', 'closed_caption', '#a93838', 'translation', true, '', false),
      'filter:label': new StringFilter('label', 'label', '#006992', 'labels', false),
      'filter:metadata': new StringFilter('metadata', 'reorder', '#999999', 'metadata', false, 'Filter by metadata'),
      'filter:events': (() => { const f = new StringFilter('events', 'filter_list', '#034e83', 'events', false, 'Search in log events'); f.displayLabel = 'log events'; return f; })(),
      'filter:text': (() => { const f = new StringFilter('text', 'dvr', '#034e83', 'text', false, 'Search in detected text'); f.displayLabel = 'detected text'; return f; })(),
      'filter:formId': new StringFilter('formId', 'reorder', '#008080', 'forms', true, '', true),
      'filter:createdDate': new DateRangeFilter({
        logger,
        key: 'createdOn',
        dateHelper: this.dateHelper,
        alias: 'Item Created',
        readableKey: 'createdOn',
      }),
      'filter:mediaCreatedOn': this.dateFilter,
      'filter:scheduledRecording.startTimeMs': new DateRangeFilter({
        logger,
        key: 'scheduledRecording.startTimeMs',
        dateHelper: this.dateHelper,
        alias: 'Planned ingest time',
        readableKey: 'scheduledRecording.startTimeMs',
      }),
      'filter:purgeDate': new DateRangeFilter({
        logger,
        key: 'purgeDate',
        dateHelper: this.dateHelper,
        alias: 'Item will be deleted on',
        readableKey: 'purgeDate',
      }),
      'filter:modifiedOn': new DateRangeFilter({
        logger,
        key: 'modifiedOn',
        dateHelper: this.dateHelper,
        alias: 'Modified date',
        readableKey: 'modifiedOn',
      }),
      'filter:geoLocation': this.geoShapeFilter,
      'filter:profileId': this.profileIdFilter,
      'filter:itemState': new StringFilter('itemState', 'local_offer', undefined, 'itemState', true, undefined, false),
      'filter:folder': new StringFilter('folder', 'folder', '#f94079', undefined, false, 'folders', false, true),
      'filter:fileName': new StringFilter('fileName', 'rule_folder', undefined, 'file', false, 'Filter by file name/extension'),
      'filter:archived': new ArchivedFilter(),
      'filter:orphan': new BooleanFilter('orphan', false, 'folder_open', '#792828', 'orphaned items', true),
      'filter:sourceBucket': new StringFilter('sourceBucket', 'storage', undefined, 'sourceBucket', true, 'Filter by source bucket'),
      'filter:folderId': this.folderIdFilter,
      'filter:itemId': this.itemIdFilter,
      'filter:owner': new UserFilter('owner', []),
      'filter:vclip': new BooleanFilter('vclip', false, 'content_cut', '#5b8c35', 'virtual clip', true, 'Virtual clip'),
      'filter:rule': new UsageRuleKeyFilter(() => this.ruleFilterCleanup('filter:ruleContext')),
      'filter:ruleContext': new UsageRuleContextFilter(() => this.ruleFilterCleanup('filter:rule')),
      'filter:ruleValidity': new UsageRuleValidityFilter(),
      'filter:missing': new StringFilter('missing', 'texture', '#792828', 'missing', true, 'Find items with no value for these fields', false, true),
      'filter:notEmpty': new StringFilter('notEmpty', 'grading', '#287873', 'notEmpty', true, 'Find items with non-empty fields', false, true),
      'filter:mediaDuration': new DurationRangeFilter(logger, 'mediaDuration', 'av_timer'),
      'filter:highresDeletionState': new StringFilter('highresDeletionState', 'motion_photos_off', undefined, 'highresDeletionState', true, undefined, true),
      'filter:highresDeletion': new StringFilter('highresDeletion', 'layers_clear', undefined, 'highresDeletion', true, undefined, true),
      'filter:aspectRatio': new StringFilter('aspectRatio', 'fact_check', undefined, 'aspectRatio', true, undefined, true),
      'filter:hasRelations': new BooleanFilter('hasRelations', false, 'fact_check', '#792828', 'hasRelations', true),
      'filter:onlyNonCurrentVersions': new BooleanFilter('onlyNonCurrentVersions', false, 'call_split', 'warning', 'only non-current versions', true),
      'filter:ingestedByKelda': new KeldaConfigFilter('ingestedByKelda', 'settings_applications', undefined, 'ingestedByKelda', true, 'Filter items by the Kelda that ingested them', true),
      'filter:uploadStatus': new StringFilter('uploadStatus', 'cloud_upload', '#0288D1', 'Upload Status', true, 'Filter items by upload status', true),
      'filter:pinned': new StringFilter('pinned', 'push_pin', '#FF9800', 'Pinned Items', true, 'Filter items by whether they are pinned or not', true),
    };
  }

  private ruleFilterCleanup(key: 'filter:rule' | 'filter:ruleContext') {
    const filter = this.defaultFilters[key];
    if (filter && filter.hasValue) {
      filter.clearValue();
    }
  }

  public setUsers(users: UserInfoDto[]) {
    this.defaultFilters['filter:owner'].users = users;
  }

  /**
   * We need to explicitly include the formId filter when we detect a filter for a metadata
   * field - this should be called before serializing.
   *
   * It will also take care of removing the formId if no custom values are present.
   */
  checkMetadataForm(allMdfs: MetaDataSchema[], searchDateOption?: SearchDateOption) {
    const formIdFilter = this.defaultFilters['filter:formId'];
    let formToCheck: MetaDataSchema | undefined = undefined;
    for (const key in this.customFilters) {
      const filter = this.customFilters[key];

      // Checks whether the filter has a value. If it's the current date filter and a dynamic search date option is selected,
      // then we consider it as having a value.
      // For backward compatibility, also check the legacy single dateFilter
      const hasValue = filter?.hasValue || (this.dateFilter && filter?.key === this.dateFilter.key && searchDateOption);
      if (hasValue && filter.metadataFormId && filter.metadataFormId !== DEFAULT_MDF_ID) {
        const mdf = allMdfs?.find(s => s.id === filter.metadataFormId);
        if (mdf) {
          formToCheck = mdf;
          break;
        }
      }
    }

    if (formToCheck && !formToCheck.isSubtype && (!formIdFilter.hasValue || formIdFilter.getValue() !== formToCheck.id)) {
      formIdFilter.alias = formToCheck.label;
      formIdFilter.parseValue(formToCheck.id, formIdFilter.hasSticky);
    } else if (formIdFilter.hasValue && formIdFilter.getValue() === DEFAULT_MDF_ID) {
      formIdFilter.clearValue();
    }
  }

  /**
   * Sets a date filter. With multiple date filter support, this is now additive.
   * The legacy dateFilter property is maintained for backward compatibility.
   */
  public setDateFilter({ key, label, dateHelper, mdf }: { key: string, label: string, dateHelper: DateHelper, mdf?: MetaDataSchema }) {
    const dateFilterType = filterTypeForKey(key);
    const isDefaultFilter = isDefaultFilterType(dateFilterType);

    // Check if this filter already exists
    const existingFilter = isDefaultFilter ? this.defaultFilters[dateFilterType] : this.customFilters[key];
    if (existingFilter && existingFilter instanceof DateRangeFilter) {
      // Filter already exists, just update the legacy dateFilter reference for backward compatibility
      this.dateFilter = existingFilter;
      return;
    }

    const isFromCustomForm = mdf?.id !== DEFAULT_MDF_ID && !mdf?.isSubtype;
    let newDateFilter: DateRangeFilter;

    if (isDefaultFilter) {
      const df = this.defaultFilters[dateFilterType];
      assert(df instanceof DateRangeFilter, 'Expecting DateRangeFilter for key: ' + key);
      newDateFilter = df;
      if (!newDateFilter.alias) {
        newDateFilter.alias = label;
      }
    } else if (key in this.customFilters) {
      newDateFilter = this.customFilters[key] as DateRangeFilter;
    } else {
      const readableKey = mdf?.fields.find(f => f.id === key)?.fieldId ?? key;
      const customDateFilter = new DateRangeFilter({
        logger: this.logger,
        key,
        dateHelper,
        alias: label,
        readableKey,
      });
      customDateFilter.metadataFormId = mdf?.id;
      customDateFilter.alias = label;
      newDateFilter = customDateFilter;
      this.customFilters[key] = newDateFilter;
    }

    // Update the legacy dateFilter property for backward compatibility
    this.dateFilter = newDateFilter;

    // If the date filter field is in a custom schema, we need to change the formId filter to point to that schema.
    if (mdf && isFromCustomForm) {
      this.defaultFilters['filter:formId'].alias = mdf.label;
      this.defaultFilters['filter:formId'].parseValue(mdf.label, this.defaultFilters['filter:formId'].hasSticky);
    } else if (this.defaultFilters['filter:formId'].getValue() !== DEFAULT_MDF_ID) {
      // Otherwise, if the formId is set to a custom schema (e.g. from a previous custom schema date field), then it
      // needs to be cleared so that the search is not limited to just items from the custom schema
      this.defaultFilters['filter:formId'].clearValue();
    }
  }

  /**
   * Gets all active date filters (both default and custom)
   */
  public getActiveDateFilters(): DateRangeFilter[] {
    const dateFilters: DateRangeFilter[] = [];

    // Check default filters for date filters
    for (const key in this.defaultFilters) {
      const filter = this.defaultFilters[key as keyof DefaultFilters];
      if (filter instanceof DateRangeFilter && filter.hasValue) {
        dateFilters.push(filter);
      }
    }

    // Check custom filters for date filters
    for (const key in this.customFilters) {
      const filter = this.customFilters[key];
      if (filter instanceof DateRangeFilter && filter.hasValue) {
        // Avoid duplicates (in case it's also in defaultFilters)
        if (!dateFilters.some(df => df.key === filter.key)) {
          dateFilters.push(filter);
        }
      }
    }

    return dateFilters;
  }
}

/**
 * WARNING: We have Vue code depending on reactive updates
 * from this class. This means updates to fields must be done
 * in ways supported by Vue reactivity, eg., using Array.splice
 * instead of direct index assignment.
 */
export abstract class Filter<T> {

  /**
   * A sticky filter value cannot be removed by the user.
   */
  public stickiedValues: T[] = [];

  /**
   * Whether the icon is a custom made svg, or a built-in MD icon.
   */
  public customIcon = false;

  /**
   * Whether a filter should be treated as single value only
   */
  public singleValue = false;

  /**
   * Internal data structure for filters.
   */
  protected value: Array<T>;

  /**
   * Field Type if this filter is created based off a {@link MdfFieldEntity}
   */
  public fieldType?: FieldType;
  /**
   * Whether this filter should serialize and be part of the final search string
   */
  public shouldSerialize = true;
  /**
   * Key will be used to serialize, and it'll be visible to the user
   * if its part of a custom filter and alias is not set.
   */
  public key: string;
  /**
   * Alias is preferred if present to present to the user in the client
   */
  public alias?: string;
  /**
   * Display label shown as placeholder text on the advanced search filters
   */
  public displayLabel?: string;
  /**
   * Icon will be placed as part of the filter in the UI if present
   */
  public icon?: string;
  /**
   * Color will be used as part of the filter in the UI if present
   */
  public color?: string;

  /**
   * This denotes whether this filter should be generated as part
   * of the advanced search menu.
   */
  public isHidden?: boolean = false;

  public metadataFormId: string | undefined = undefined;

  /**
   * By default all filters serialize into a final search string by the 'key:value' notation.
   */
  public serializeMode = SerializeMode.String;

  /**
   * @param key The key this filter can be fetched by. Also used to generate key:value pair that is serialized
   *            and added to the search string sent to the server.
   */
  constructor(key: string) {
    this.key = key;
    this.value = [];
  }

  /**
   * Return a readable string from a given value of type T.
   */
  abstract getReadableValue(value: T, index: number): string;

  /**
   * Return the serialized version of this Filter.
   */
  abstract get serializedValue(): string;

  /**
   * Return the query params version of this Filter, if applicable.
   */
  get queryParameters(): Record<string, string> {
    return {};
  }

  get hasSticky(): boolean {
    return this.stickiedValues.length > 0;
  }

  /**
   * This will be called on the filter when parsing the search string,
   * and should be responsible for type checking and validation.
   */
  abstract parseValue(val: string, makeSticky?: boolean): void;

  /**
   * This should return the values that are displayed to the user in the search panel.
   * If the filter only supports one value it should return it as the only value in the array.
   */
  get filterValues(): FilterViewValue[] {
    const vals: FilterViewValue[] = [];
    for (let i = 0; i < this.value.length; i++) {
      const v: T = this.value[i];
      vals.push({
        value: this.getReadableValue(v, i),
        isSticky: this.isSticky(v)
      });
    }
    return vals;
  }

  /**
   * Whether the filter currently has a value
   */
  get hasValue(): boolean {
    return this.value.length > 0;
  }

  isSticky(val: T) {
    return this.value.includes(val) && this.stickiedValues.includes(val);
  }

  /**
   * Return the requested value. If index is not supplied, first value
   * in the array will be returned.
   *
   * TODO: Having index as an optional here has caused bugs. Make it required.
   */
  getValue(index?: number): T | undefined {
    if (!this.hasValue)
      return undefined;

    if (index && index < this.value.length) {
      return this.value[index];
    }

    return this.value[0];
  }

  /**
   * Filters can choose to override this, if they want a string
   * representation of the filter. This is for example useful for
   * mapping range filter between metadata editor and filters without
   * losing strong types.
   *
   * Will return empty string if no value is present.
   */
  getValueAsString(index?: number): string {
    const value = this.getValue(index);
    // We cannot test 'value' directly as it will return false
    // for perfectly valid boolean negative values
    return (value !== undefined && value !== null) ? value + '' : '';
  }

  /**
   * Will clear the full value array for this filter, keeping any stickied values
   */
  clearValue() {
    for (let j = this.value.length - 1; j >= 0; j--) {
      if (!this.isSticky(this.value[j])) {
        this.value.splice(j, 1);
      }
    }
  }

  /**
   * Clear all, including stickies.
   */
  forceClearAll() {
    this.value = [];
  }

  /**
   * Check if given value is present.
   */
  containsValue(val: T) {
    return this.value.includes(val);
  }

  /**
   * Set all values at once. Use with care.
   *
   * It allows a string as a parameter signature so implementors can decide
   * to implement parsing of a string to the proper type it wants, however
   * by default Filters will ignore if it gets a string so it should be implemented
   * as necessary.
   */
  setValue(val: T[] | string) {
    if (typeof val === 'string')
      return;

    this.value = val;
  }

  /**
   * Add a value to the filter array if it didn't already exist.
   *
   * Note: This is called for every value that matches a valid filter
   * when mapping filters from searchstring to filter. If you wish to alter
   * the behaviour, such is the case in the date range filter, then override
   * this method and provide your own.
   */
  addValue(val: T) {
    if (!this.value.includes(val))
      this.value.push(val);
  }

  /**
   * Remove value by index or value.
   *
   * Some filters may not support multiple values, and therefore
   * clear the entire value range no matter what parameter they receive.
   */
  removeValue(index: number | T) {
    if (typeof index === 'number' && this.value.length > index) {
      this.value.splice(index, 1);
    } else {
      const indexIfFound = this.value.indexOf(index as T);
      if (indexIfFound > -1) {
        this.value.splice(indexIfFound, 1);
      }
    }
  }
}

/**
 * A range filter represents a range between start and end of type T.
 *
 * This abstract class takes care of the parsing and serializing. If user visible
 * values needs to be customised, override as necessary.
 */
export abstract class RangeFilter<T> extends Filter<T> {

  constructor(protected logger: Logger, key: string) {
    super(key);
    this.isHidden = true;
    this.shouldSerialize = true;
  }

  /**
   * Needed to be able to parse string to the correct type during
   * parsing of search string.
   */
  abstract getTypeFrom(val: string): T;

  /**
   * Return a single serialized value based on type T. This is used to serialized
   * the value down to [xxx to xxx] notation, and also acts as the default
   * xxx to xxx value the user sees in the filter list.
   */
  abstract getSingleSerializedValue(val: T): string;

  /**
   * Returns the string presentation of this filter in [xxx to xxx] format.
   */
  getValueAsString(index?: number): string {
    if (!this.hasValue) return '';
    return `[${this.getSingleSerializedValue(this.value[0])} to ${this.getSingleSerializedValue(this.value[1])}]`;
  }

  /**
   * Range filters do not support adding values this way.
   */
  addValue(value: T) {
    throw Error('Range filters do not support adding values this way, it can only contain one value.');
  }

  /**
   * Range filters only have one value, so we clear it if this gets called.
   */
  removeValue(index: number) {
    this.clearValue();
  }

  /**
   * By default, show the raw values to the user.
   *
   * Override for more control.
   */
  get filterValues(): FilterViewValue[] {
    if (!this.hasValue) return [];
    return [{ value: `${this.value[0]} to ${this.value[1]}`, isSticky: false }];
  }

  /**
   * Range filters need to implement this.setValue() and expect
   * a [xxx to xxx] pattern value.
   */
  parseValue(val: string) {
    this.setValue(val);
  }

  /**
   * Default behaviour is just casting the value to a string.
   */
  getReadableValue(value: T): string {
    return '' + value;
  }

  /**
   * Set the value for this filter.
   * @param values The 2 values in an array, from at 0 and to at 1,
   *               or a parseable string of format [xxx to xxx]
   * @param validate An optional validation function if you want to validate
   *                 the values after typecast. Useful for checking for valid dates for example.
   */
  setValue(values: T[] | string, validate?: (val: T) => boolean) {
    let newValues: T[] = [];
    if (typeof values === 'string') {
      const parsed = rangeQueryPatternRegex.exec(values);
      if (parsed && parsed.length === 3) {
        newValues.push(this.getTypeFrom(parsed[1].trim()));
        newValues.push(this.getTypeFrom(parsed[2].trim()));
      } else {
        this.logger.warn('Invalid range found in search string.');
        return;
      }
    } else {
      newValues = values;
    }

    if (validate) {
      if (!validate(newValues[0]) || !validate(newValues[1])) {
        this.logger.warn('Invalid values found: ' + JSON.stringify(newValues, null, 2));
        return;
      }
    }
    super.setValue(newValues);
  }

  get serializedValue(): string {
    if (this.hasValue) {
      return `${this.key}:${this.getValueAsString()}`;
    }
    return '';
  }
}

export class StringFilter extends Filter<string> {

  constructor(key: string, public icon?: string, public color?: string, public alias?: string, public isHidden?: boolean,
    public tooltip?: string, singleValueOnly?: boolean, public shouldSerialize = true) {
    super(key);

    this.singleValue = singleValueOnly ?? false;

    if (!color)
      this.color = '#008080';

    if (!tooltip)
      this.tooltip = `Search in ${this.alias ? this.alias : this.key}`;
  }

  parseValue(val: string, makeSticky = false) {
    if (this.singleValue) {
      // Fragile code warning:
      // We have vue code depending on this.value being reactive, and
      // so we cannot do non-reactive update like `value[0] = val`;
      this.value.splice(0, 1, val);
    } else {
      this.addValue(val);
    }

    if (makeSticky && !this.stickiedValues.includes(val)) {
      this.stickiedValues.push(val);
    }
  }

  /**
   * These are used as a two-way binding v-model in the advanced search menu, to support
   * comma separated multiple values while keeping the original model in sync
   *
   * Stickied values are excluded, since they cannot be removed.
   */
  get commaParsedValue(): string {
    const nonStickied: string[] = [];
    for (const val of this.value) {
      if (!this.isSticky(val)) {
        nonStickied.push(val);
      }
    }
    return nonStickied.join(',');
  }

  /**
   * These are used as a two-way binding v-model in the advanced search menu, to support
   * comma separated multiple values while keeping the original model in sync
   *
   * Vuetify clear icons send null strings as value, so we need to handle for that case.
   *
   * Stickied values are preserved.
   */
  set commaParsedValue(val: string) {
    if (val === undefined || val === null || val.trim().length === 0) {
      this.value = [...this.stickiedValues];
      return;
    }

    const sanitized = val.split(',').map((v: string) => v.trim());
    this.value = [...this.stickiedValues, ...sanitized.filter((v: string) => !isEmpty(v))];
  }

  /**
   * Used as a placeholder text in the advanced search menu
   */
  get description(): string {
    return capitalize(this.displayLabel ?? (this.alias ? this.alias : this.key));
  }

  /**
   * Get the serialized value for this Filter.
   */
  get serializedValue(): string {
    let final = '';
    for (const val of this.value) {
      const value = val.indexOf(' ') !== -1 ? `"${val}"` : val;
      const sticky = this.isSticky(val) ? `${filterDelimiter}${extraInfoSticky}` : '';
      final += `${this.key}:${value}${sticky} `;
    }
    return final.trim();
  }

  getReadableValue(value: string): string {
    if (!this.fieldType) {
      if (this.key === StaticFilterKeywords.FormId)
        return `type: ${this.alias}`;
      return `${value}`;
    }
    return `${this.alias ? this.alias : this.key}: ${value}`;
  }
}

export class UserFilter extends StringFilter {

  constructor(fieldId: string, public users: UserInfoDto[]) {
    super(fieldId);
    this.isHidden = true;
    this.icon = 'person';
    this.singleValue = true;
  }

  get user() {
    return this.users.find(u => u.id === this.getValueAsString()) || null;
  }

  parseValue(guid: string) {
    if (isValidGUID(guid)) {
      super.parseValue(guid);
    }
  }

  getReadableValue(userId: string) {
    return `${this.alias ?? 'Owner'}: ${this.user ? getUserDisplayName(this.user) : userId}`;
  }
}

abstract class UsageRuleFilter extends StringFilter {
  constructor(key: 'rule' | 'ruleContext', icon: string, color: string, tooltip: string, private onValueClear: () => void) {
    super(key, icon, color, key, true, tooltip, true);
  }

  abstract getReadableValue(value: string): string;

  clearValue() {
    super.clearValue();
    this.onValueClear();
  }

  removeValue(index: number | string) {
    super.removeValue(index);
    this.onValueClear();
  }
}

export class UsageRuleKeyFilter extends UsageRuleFilter {
  private rules: TimedMdfUsageRule[] = [];
  constructor(onValueClear: () => void) {
    super('rule', 'rule', '#10ad4f', 'Usage rule', onValueClear);
  }

  setRules(rules: TimedMdfUsageRule[]) {
    this.rules = rules;
  }

  getReadableValue(ruleKey: string) {
    const prefix = 'Rule: ';
    const ruleLabel = this.rules.find(r => r.identifier.key === ruleKey)?.identifier.label;
    return `${prefix}${ruleLabel ?? ruleKey}`;
  }
}

export class UsageRuleContextFilter extends UsageRuleFilter {
  constructor(onValueClear: () => void) {
    super('ruleContext', 'gavel', '#10ada0', 'Usage rule context', onValueClear);
  }

  getReadableValue(context: string) {
    return `Rule context: ${context}`;
  }
}

export class TypeFilter extends Filter<SearchCategory> {

  constructor() {
    super('type');

    this.isHidden = true;
    this.color = '#21859c';
    this.singleValue = true;
  }

  clearValue() {
    this.value = [...this.stickiedValues];
  }

  removeValue(val: number | SearchCategory) {
    const index = typeof val === 'number' ? val : this.value.indexOf(val);
    this.value.splice(index, 1);
  }

  addValue(itemType: SearchCategory) {
    if (this.hasSticky || !isSearchCategory(itemType)) {
      return;
    }
    if (this.value.indexOf(itemType) === -1) {
      this.value.push(itemType);
    } else {
      this.removeValue(itemType);
    }
  }

  parseValue(val: string, makeSticky = false) {
    if (hasSearchCategoryOnly(val)) {
      const categories = val.split(',').map(v => v as SearchCategory);
      categories.forEach(c => {
        if (!this.value.includes(c)) this.value.push(c);
        if (makeSticky && !this.stickiedValues.includes(c)) this.stickiedValues.push(c);
      });
    }
  }

  get serializedValue(): string {
    if (this.value.length > 0) {
      const sticky = this.hasSticky ? `${filterDelimiter}${extraInfoSticky}` : '';
      return `${this.key}:${this.value.join(',')}${sticky}`;
    }
    return '';
  }

  get selectedCategories(): SearchCategory[] {
    return [...this.value];
  }

  get filterValues(): FilterViewValue[] {
    return [{
      // Timeline and ClipList share the same label "Sequence"
      // and both values are stored in the filter when "Sequence" is checked,
      // so Set is used to avoid showing duplicate labels in the UI.
      value: [...new Set(this.value.map(v => this.getReadableValue(v)))].join(' / '),
      isSticky: this.hasSticky
    }];
  }

  getReadableValue(value: SearchCategory): string {
    return `${getLabelForSearchCategory(value)}`;
  }
}

export class ItemIdFilter extends StringFilter {

  constructor() {
    super('itemId', 'sort', '#06404c', undefined, true, undefined, true, true);
    this.serializeMode = SerializeMode.Parameters;
  }

  get itemId(): string | undefined {
    return this.value.length === 1 ? this.value[0] : undefined;
  }

  get queryParameters(): Record<string, string> {
    return this.itemId ? { itemId: this.itemId } : {};
  }
}
export class ProfileIdFilter extends StringFilter {

  private nameOverride: string | null = null;

  constructor() {
    super('profileId');
    this.color = '#424874';
    this.icon = 'person';
    this.isHidden = true;

    // For now there's no use case to allow multiple id searches
    this.singleValue = true;
  }

  /**
   * If we find a value after the delimiter, we assume its the name of the person.
   */
  parseValue(val: string) {
    const indexOfPipe = val.indexOf(filterDelimiter);
    if (indexOfPipe > 0) {
      const name = val.substring(indexOfPipe + 1);
      if (name.length > 0)
        this.nameOverride = name;
    }

    this.value[0] = val;
  }

  getReadableValue(value: string): string {
    // Execute decodeURI on name as it comes pre-encoded to avoid parsing issues because of special characters
    return this.nameOverride !== null ? `${decodeURI(this.nameOverride)}` : `${value}`;
  }

  clearValue() {
    super.clearValue();
    this.nameOverride = null;
  }
}

/**
 * Supports [123 to 456] type values.
 */
export class NumberRangeFilter extends RangeFilter<number> {

  /** @override */
  setValue(values: number[] | string, validate?: (val: number) => boolean) {
    let newValues: number[] = [];
    if (typeof values === 'string') {
      const parsed = rangeQueryPatternRegex.exec(values);
      let from: string;
      let to: string;
      if (!parsed || parsed.length !== 3) {
        // [xxx to xxx]
        const rangeList = values.replace('[', '').replace(']', '').split(' ');
        from = rangeList[0].trim();
        to = rangeList[2].trim();
      } else {
        from = parsed[1].trim();
        to = parsed[2].trim();
      }

      newValues.push(this.getTypeFrom(from));
      newValues.push(this.getTypeFrom(to));

    } else {
      newValues = values;
    }

    if (validate) {
      if (!validate(newValues[0]) || !validate(newValues[1])) {
        this.logger.warn('Invalid values found: ' + JSON.stringify(newValues, null, 2));
        return;
      }
    }

    if (newValues.length === 2 && !isNaN(newValues[0]) && !isNaN(newValues[1]) && newValues[0] > newValues[1]) {
      this.shouldSerialize = false;
    } else if (isNaN(newValues[0]) && isNaN(newValues[1])) {
      newValues = [];
    } else {
      this.shouldSerialize = true;
    }

    this.value = newValues;
  }

  getTypeFrom(value: string): number {
    return parseInt(value);
  }

  getValue(index: number): number | undefined {
    return isNaN(this.value[index]) || this.value[index] < 0 ? undefined : this.value[index];
  }

  getSingleSerializedValue(value: number): string {
    return isNaN(value) ? 'undefined' : value + '';
  }

  get filterValues(): FilterViewValue[] {
    if (!this.hasValue || (this.value[0] === undefined && this.value[1] === undefined) || isNaN(this.value[0]) && isNaN(this.value[1]) && this.value[0] >= this.value[1]) return [];
    let value: string;
    if (isNaN(this.value[0])) value = `up to ${this.value[1]}`;
    else if (isNaN(this.value[1])) value = `at least ${this.value[0]}`;
    else value = `${this.value[0]} to ${this.value[1]}`;
    value = `${this.alias ?? this.key}: ${value}`;
    return [{ value, isSticky: false }];
  }
}

export class DurationRangeFilter extends NumberRangeFilter {

  constructor(logger: Logger, key: string, public icon?: string) {
    super(logger, key);
    this.color = '#544040';
  }


  get filterValues(): FilterViewValue[] {
    const temp = super.filterValues;
    if (!temp.length) return temp;
    // removing field label part and adding 's' to indicate seconds
    const value = temp[0].value.split(':')[1] + 's';
    return [{ value, isSticky: temp[0].isSticky }];
  }
}

/**
 * A filter for pure boolean fields.
 */
export class BooleanFilter extends Filter<boolean | 'yes' | 'no'> {

  constructor(key: string, public customIcon = false, public icon?: string, public color?: string, alias?: string, public isHidden?: boolean, public tooltip?: string) {
    super(key);
    this.singleValue = true;
    if (alias) {
      this.alias = alias;
    }

    if (!tooltip) {
      this.tooltip = `Search in ${this.alias ? this.alias : this.key}`;
    }
  }

  getReadableValue(value: boolean, index: number): string {
    return value ? `Include ${this.alias ? this.alias : this.key}` : `Exclude ${this.alias ? this.alias : this.key}`;
  }

  get serializedValue(): string {
    if (this.hasValue) {
      return `${this.key}:${this.getValueAsString()}`;
    }
    return '';
  }

  /**
   * Used as a placeholder text in the advanced search menu
   */
  get description(): string {
    return capitalize(this.alias ? this.alias : this.key);
  }

  addValue(val: boolean | 'yes' | 'no') {
    this.clearValue();
    this.value.push(val);
  }

  parseValue(val: string): void {
    // Since booleans are binary, it makes no sense
    // to allow more than one value, so let's make
    // sure the array has at most one element
    this.clearValue();
    const toBool = parseBool(val);
    if (toBool !== null) {
      this.addValue(toBool);
    }
  }
}

export class UsageRuleValidityFilter extends BooleanFilter {
  constructor() {
    super('ruleValid', false, 'fact_check', '#d11717', 'Usage rule validity', true);
  }

  getReadableValue(value: boolean) {
    return `Rule validity: ${value ? 'valid' : 'expired'}`;
  }
}

export class ArchivedFilter extends Filter<ArchivedFilterType> {
  tooltip = 'Search in archived';
  constructor() {
    super('archived');
    this.singleValue = true;
    this.customIcon = true;
    this.icon = 'archive-24px';
    this.color = '#29357b';
    this.isHidden = true;
  }

  getReadableValue(value: ArchivedFilterType): string {
    let ret = '';
    switch (value) {
      case 'yes':
        ret = `Include ${this.alias ? this.alias : this.key}`;
        break;
      case 'no':
        ret = `Exclude ${this.alias ? this.alias : this.key}`;
        break;
      case 'failed':
        ret = `Failed ${this.alias ? this.alias : this.key}`;
        break;
    }
    return ret;
  }

  get serializedValue(): string {
    if (this.hasValue) {
      return `${this.key}:${this.getValueAsString()}`;
    }
    return '';
  }

  /**
   * Used as a placeholder text in the advanced search menu
   */
  get description(): string {
    return capitalize(this.alias ? this.alias : this.key);
  }

  addValue(val: ArchivedFilterType) {
    this.clearValue();
    this.value.push(val);
  }

  parseValue(val: string): void {
    this.clearValue();
    const value = parseArchivedFilterValue(val);
    if (value !== null) {
      this.addValue(value);
    }
  }
}

export class GeoShapeRangeFilter extends RangeFilter<TimedCoordinate> {
  constructor(logger: Logger, key: string, icon?: string) {
    super(logger, key);
    this.isHidden = true;
    this.icon = icon ?? 'place';
    this.color = '#4CAF50';
  }

  /**
   * Needed to be able to parse string to the correct type during
   * parsing of search string.
   */
  getTypeFrom(val: string): TimedCoordinate {
    const [lat, lng] = val.split(',').map(Number);
    return { lat, lng };
  }

  /**
   * Return a single serialized value based on type T. This is used to serialized
   * the value down to [xxx to xxx] notation, and also acts as the default
   * xxx to xxx value the user sees in the filter list.
   */
  getSingleSerializedValue(val: TimedCoordinate): string {
    return `${val.lat},${val.lng}`;
  }

  /**
   * By default, show the raw values to the user.
   *
   * Override for more control.
   */
  get filterValues(): FilterViewValue[] {
    if (!this.hasValue) return [];

    const rangeStart = this.value[0];
    const rangeEnd = this.value[1];

    return [{ value: `${this.getReadableValue(rangeStart)} to ${this.getReadableValue(rangeEnd)}`, isSticky: false }];
  }

  /**
   * Default behaviour is just casting the value to a string.
   */
  getReadableValue(value: TimedCoordinate): string {
    return `(${value.lat.toFixed(2)}, ${value.lng.toFixed(2)})`;
  }
}

type DateRangeFilterParams = {
  logger: Logger,
  key: string,
  dateHelper: DateHelper,
  icon?: string,
  color?: string,
  alias?: string,
  readableKey: string,
};

/**
 * Supports [date.toISOString() to date.toISOString()] values.
 * Also supports relative syntax strings like "[-7d to +0d]" for server-side processing.
 */
export class DateRangeFilter extends RangeFilter<Date | string> {

  // Serves as a single entry point to determine whether a value has changed in the date filter.
  // This allows us to watch this property directly from a Vue component,
  // taking control on when the value changes on the inside. The only alternative is using { deep: true } as
  // a watcher option, but then we get many unnecessary event triggers.
  public changedCounter = 0;
  private dateHelper = new DateHelper();
  public readonly readableKey: string;

  constructor(params: DateRangeFilterParams) {
    super(params.logger, params.key);
    this.isHidden = true;
    this.icon = params.icon ?? 'calendar_today';
    this.color = params.color ?? '#2196F3';
    this.alias = params.alias ?? '';
    this.dateHelper = params.dateHelper;
    this.readableKey = params.readableKey;
  }

  getSingleSerializedValue(value: Date | string): string {
    return value instanceof Date ? value.toISOString() : value;
  }

  getTypeFrom(value: string): Date | string {
    return new Date(value);
  }

  /**
   * Override getValueAsString to handle range syntax strings
   * For DateRangeFilter, we should return the full range syntax to maintain consistency
   */
  getValueAsString(index?: number): string {
    if (!this.hasValue) return '';

    // Check if we have range syntax stored as string
    const firstValue = this.value[0];
    if (this.value.length === 1 && isDateRangeSyntax(firstValue)) {
      // Return the full range syntax WITH brackets to maintain consistency
      return firstValue;
    }

    // Handle normal Date objects
    return super.getValueAsString(index);
  }

  setValue(values: Date[] | string, validate?: (val: Date | string) => boolean) {
    // Check if this is a range syntax string
    if (typeof values === 'string' && isDateRangeSyntax(values)) {
      // Check if it contains relative syntax or wildcards
      // Wildcards: *, Relative patterns: +Xd, -Xd, +Xh, -Xh, +Xw, etc.
      const hasWildcard = values.includes('*');
      const hasRelativePattern = relativeTimeDetectPatternRegex.test(values);

      if (hasWildcard || hasRelativePattern) {
        // Store relative/wildcard syntax directly for server-side processing
        this.value = [values];
        this.changedCounter++;
        return;
      } else {
        // Try to parse as normal ISO date range strings into Date objects
        const rangeMatch = dateRangePatternRegex.exec(values);
        if (rangeMatch) {
          const startStr = rangeMatch[1].trim();
          const endStr = rangeMatch[2].trim();

          // Try to parse as dates
          const startDate = new Date(startStr);
          const endDate = new Date(endStr);

          if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
            // Valid dates - proceed with normal Date array handling
            // This will allow isFullYear/isFullMonth checks to work
            super.setValue([startDate, endDate], validate);
            this.changedCounter++;
            return;
          }
        }

        // If parsing failed, store as string (fallback)
        this.value = [values];
        this.changedCounter++;
        return;
      }
    }

    // Handle single date arrays for partial ranges
    if (Array.isArray(values) && values.length === 1 && values[0] instanceof Date) {
      // Create partial range syntax: [date to *] (start-only range)
      const partialRangeSyntax = `[${values[0].toISOString()} to *]`;
      this.value = [partialRangeSyntax];
      this.changedCounter++;
      return;
    }

    // Try to get date range if values is a SearchDateOption enum
    if (typeof values === 'string') {
      const dates = getDateRange(values as SearchDateOption);
      if (!isEmpty(dates)) {
        super.setValue(dates, validate);
        this.changedCounter++;
        return;
      }
    }

    // Fallback: set values as is
    super.setValue(values, validate);
    this.changedCounter++;
  }

  parseValue(val: string) {
    // Check if the value is "past" or "future"
    if (val.toLowerCase() === 'past' || val.toLowerCase() === 'future') {
      const dateOption = val.toUpperCase() as SearchDateOption;
      const dates = getDateRange(dateOption);
      if (!isEmpty(dates)) {
        super.setValue(dates, isValidDate);
        this.changedCounter++;
        return;
      }
    }
    this.setValue(val, isValidDate);
  }

  /**
   * Set relative date range with server-side processing
   * Generates range syntax like "[-7d to +0d]" or "[2025-01-01 to +30d]" that server can parse
   */
  setRelativeRange(startFilter?: RelativeDateFilter, endFilter?: RelativeDateFilter) {
    // Build range syntax string for server
    const relativeSyntaxString = this.buildRelativeSyntaxString(startFilter, endFilter);

    if (relativeSyntaxString) {
      // Use setValue which will detect and handle the relative syntax
      this.setValue(relativeSyntaxString);
    }
  }

  /**
   * Build relative date range syntax string for server-side processing
   * Creates syntax like "[-7d to +0d]" for relative dates, or "[2025-01-01 to +30d]" for mixed ranges
   *
   * @param startFilter - Optional start filter (absolute or relative)
   * @param endFilter - Optional end filter (absolute or relative)
   * @returns Range syntax string ready for server parsing, or empty string if no filters
   *
   */
  private buildRelativeSyntaxString(startFilter?: RelativeDateFilter, endFilter?: RelativeDateFilter): string {
    // No filters provided
    if (!startFilter && !endFilter) {
      return '';
    }

    let syntaxString = '';

    // Handle range case (both filters present)
    if (startFilter && endFilter) {
      syntaxString = '[';

      // Build start part
      if (startFilter.type === 'relative') {
        syntaxString += this.generateRelativeFilterSyntax(startFilter);
      } else if (startFilter.absoluteDate) {
        syntaxString += startFilter.absoluteDate.toISOString();
      } else {
        syntaxString += '*';
      }

      syntaxString += ' to ';

      // Build end part
      if (endFilter.type === 'relative') {
        syntaxString += this.generateRelativeFilterSyntax(endFilter);
      } else if (endFilter.absoluteDate) {
        syntaxString += endFilter.absoluteDate.toISOString();
      } else {
        syntaxString += '*';
      }

      syntaxString += ']';
    }
    // Handle start-only filter
    else if (startFilter) {
      if (startFilter.type === 'relative') {
        syntaxString = `[${this.generateRelativeFilterSyntax(startFilter)} to *]`;
      } else if (startFilter.absoluteDate) {
        syntaxString = `[${startFilter.absoluteDate.toISOString()} to *]`;
      }
    }
    // Handle end-only filter
    else if (endFilter) {
      if (endFilter.type === 'relative') {
        syntaxString = `[* to ${this.generateRelativeFilterSyntax(endFilter)}]`;
      } else if (endFilter.absoluteDate) {
        syntaxString = `[* to ${endFilter.absoluteDate.toISOString()}]`;
      }
    }

    return syntaxString;
  }

  /**
   * Generate relative syntax string from RelativeDateFilter
   * @param filter - RelativeDateFilter with value/unit/direction
   * @returns Syntax string like "+1w" or "-5h"
   */
  private generateRelativeFilterSyntax(filter: RelativeDateFilter): string {
    if (filter.type === 'absolute' && filter.absoluteDate) {
      return filter.absoluteDate.toISOString();
    } else if (filter.type === 'relative' && filter.value !== undefined && filter.value !== null && filter.unit && filter.direction) {
      return generateRelativeTimeString(filter.value, filter.unit, filter.direction);
    }
    // Handle edge cases
    return '*';
  }

  get serializedValue(): string {
    if (this.hasValue) {
      // Check if we have range syntax stored (relative or partial with wildcards)
      const firstValue = this.value[0];
      if (this.value.length === 1 && isDateRangeSyntax(firstValue)) {
        // Return the range syntax directly for server-side processing
        return `${this.key}:${firstValue}`;
      }

      // Handle normal Date objects
      if (this.value.length >= 2 && this.value[0] instanceof Date && this.value[1] instanceof Date) {
        const m0 = this.dateHelper.date(this.value[0].toISOString());
        const m1 = this.dateHelper.date(this.value[1].toISOString());
        m0.set('hour', 0);
        m0.set('minute', 0);
        m0.set('second', 0);
        m0.set('millisecond', 0);

        m1.set('hour', 23);
        m1.set('minute', 59);
        m1.set('second', 59);
        m1.set('millisecond', 999);

        return `${this.key}:[${m0.toISOString()} to ${m1.toISOString()}]`;
      }

      console.warn('DateRangeFilter.serializedValue: Unexpected value format:', this.value);
    }
    return '';
  }

  get filterValues(): FilterViewValue[] {
    if (!this.hasValue) return [];

    // Check if we have range syntax stored (relative or partial with wildcards)
    const firstValue = this.value[0];
    if (this.value.length === 1 && isDateRangeSyntax(firstValue)) {

      // Parse and display range syntax in human-readable format
      const formattedValue = this.formatRelativeSyntax(firstValue);

      const result = this.alias ? [{ value: `${this.alias}: ${formattedValue}`, isSticky: false }] : [{ value: `${formattedValue}`, isSticky: false }];
      return result;
    }

    // Handle normal Date objects
    if (this.value.length >= 2 && this.value[0] instanceof Date && this.value[1] instanceof Date) {
      const rangeStart = this.value[0];
      const rangeEnd = this.value[1];

      let formattedValue: string = '';

      const timezone = this.dateHelper.getTimezone();
      if (isSameDay(rangeStart, rangeEnd, timezone))
        formattedValue = this.dateHelper.date(rangeStart.toISOString()).format('MMMM Do YYYY');
      else if (isFullYear(rangeStart, rangeEnd, timezone))
        formattedValue = this.dateHelper.date(rangeStart.toISOString()).format('YYYY');
      else if (isFullMonth(rangeStart, rangeEnd, timezone))
        formattedValue = this.dateHelper.date(rangeStart.toISOString()).format('MMMM YYYY');
      else
        formattedValue = `${this.getReadableValue(rangeStart)} to ${this.getReadableValue(rangeEnd)}`;

      return this.alias ? [{ value: `${this.alias}: ${formattedValue}`, isSticky: false }] : [{ value: `${formattedValue}`, isSticky: false }];
    }

    console.warn('DateRangeFilter.filterValues: Unexpected value format:', this.value);
    return [];
  }

  getReadableValue(value: Date): string {
    return this.dateHelper.date(value.toISOString()).format('D MMM, YYYY');
  }

  /**
   * Format relative syntax for display in search chips
   */
  private formatRelativeSyntax(syntax: string): string {
    // Handle patterns like "[-5d to *]", "[+3d to *]", "[* to -7d]", "[2023-08-15T00:00:00.000Z to *]", etc.
    const rangeMatch = dateRangePatternRegex.exec(syntax);
    if (rangeMatch) {
      const [, start, end] = rangeMatch;

      // Helper function to format a single date/relative value
      const formatSingleValue = (value: string): { formatted: string; isTimeUnit: boolean; isDateUnit: boolean; semanticMatch?: any; relativeMatch?: any } => {
        if (value === '*') return { formatted: '', isTimeUnit: false, isDateUnit: false };

        // Try new semantic format first
        const innerSemanticMatch = parseRelativeTimeString(value);
        if (innerSemanticMatch) {
          // Check if value is 0
          if (innerSemanticMatch.value === 0) {
            // For minutes and hours: show "Now"
            if (innerSemanticMatch.unit === 'minute' || innerSemanticMatch.unit === 'hour') {
              return { formatted: 'Now', isTimeUnit: true, isDateUnit: false, semanticMatch: innerSemanticMatch };
            }
            // For days, weeks, months, years: show "Today"
            return { formatted: 'Today', isTimeUnit: false, isDateUnit: true, semanticMatch: innerSemanticMatch };
          }

          const relativeText = formatSemanticRelativeDate(innerSemanticMatch.value, innerSemanticMatch.unit, innerSemanticMatch.direction);
          const isTimeUnit = innerSemanticMatch.unit === 'minute' || innerSemanticMatch.unit === 'hour';
          const isDateUnit = !isTimeUnit;
          return { formatted: relativeText, isTimeUnit, isDateUnit, semanticMatch: innerSemanticMatch };
        }

        // Fallback to legacy format
        const legacyRegex = /^([+-])(\d+)d$/;
        const relativeMatch = legacyRegex.exec(value);
        if (relativeMatch) {
          const [, sign, days] = relativeMatch;
          const daysNum = parseInt(days, 10);
          if (daysNum === 0) {
            return { formatted: 'Today', isTimeUnit: false, isDateUnit: true, relativeMatch };
          }
          const dayText = daysNum === 1 ? 'day' : 'days';
          const formatted = sign === '+'
            ? `${daysNum} ${dayText} from now`
            : `${daysNum} ${dayText} ago`;
          return { formatted, isTimeUnit: false, isDateUnit: true, relativeMatch };
        }

        // Handle absolute date - format nicely without timestamp
        try {
          const date = new Date(value);
          if (!isNaN(date.getTime())) {
            return { formatted: this.dateHelper.date(value).format('D MMM, YYYY'), isTimeUnit: false, isDateUnit: false };
          }
        } catch (e) {
          // Ignore error
        }

        return { formatted: value, isTimeUnit: false, isDateUnit: false };
      };

      const startFormatted = formatSingleValue(start);
      const endFormatted = formatSingleValue(end);

      // Check if both start and end are the same
      if (start !== '*' && end !== '*') {
        // Special case: both are "Now" (0 minutes/hours regardless of direction)
        if (startFormatted.formatted === 'Now' && endFormatted.formatted === 'Now') {
          return 'Now';
        }

        // Special case: both are "Today" (0 days/weeks/months/years regardless of direction)
        if (startFormatted.formatted === 'Today' && endFormatted.formatted === 'Today') {
          return 'Today';
        }

        // Compare semantic values if both are semantic
        if (startFormatted.semanticMatch && endFormatted.semanticMatch) {
          const startMatch = startFormatted.semanticMatch;
          const endMatch = endFormatted.semanticMatch;

          // Check if same value, unit, and direction
          if (startMatch.value === endMatch.value &&
            startMatch.unit === endMatch.unit &&
            startMatch.direction === endMatch.direction) {
            // Both are the same relative value
            return startFormatted.formatted;
          }
        }

        // Compare legacy format values if both are legacy
        if (startFormatted.relativeMatch && endFormatted.relativeMatch) {
          const [, startSign, startDays] = startFormatted.relativeMatch;
          const [, endSign, endDays] = endFormatted.relativeMatch;

          if (startSign === endSign && startDays === endDays) {
            // Both are the same relative value
            return startFormatted.formatted;
          }
        }

        // Compare absolute dates if both are absolute
        if (!startFormatted.semanticMatch && !startFormatted.relativeMatch &&
          !endFormatted.semanticMatch && !endFormatted.relativeMatch) {
          if (startFormatted.formatted === endFormatted.formatted) {
            return startFormatted.formatted;
          }
        }
      }

      // Build the range display with "From ... to ..." format
      const parts: string[] = [];

      if (startFormatted.formatted) {
        parts.push(`From ${startFormatted.formatted}`);
      }

      if (endFormatted.formatted) {
        parts.push(`to ${endFormatted.formatted}`);
      }

      return parts.length > 0 ? parts.join(' ') : syntax;
    }

    // Handle single relative patterns (new semantic format: "+1w", "-5h", "+30m")
    const semanticMatch = parseRelativeTimeString(syntax);
    if (semanticMatch) {
      // Check if value is 0 in any unit
      if (semanticMatch.value === 0) {
        // For minutes and hours: show "Now"
        if (semanticMatch.unit === 'minute' || semanticMatch.unit === 'hour') {
          return 'Now';
        }
        // For days, weeks, months, years: show "Today"
        return 'Today';
      }
      return formatSemanticRelativeDate(semanticMatch.value, semanticMatch.unit, semanticMatch.direction);
    }

    // Handle legacy single relative patterns like "+3d" or "-5d"
    const legacySingleRegex = /^([+-])(\d+)d$/;
    const legacySingleMatch = legacySingleRegex.exec(syntax);
    if (legacySingleMatch) {
      const [, sign, days] = legacySingleMatch;
      const daysNum = parseInt(days, 10);
      if (daysNum === 0) return 'Today';
      const dayText = daysNum === 1 ? 'day' : 'days';
      return sign === '+' ? `${daysNum} ${dayText} from now` : `${daysNum} ${dayText} ago`;
    }
    return syntax; // Fallback
  }
}

export class ChoiceFilter extends StringFilter {

  private field: MdfFieldEntity;
  private alternative: Alternative | undefined;

  constructor(field: MdfFieldEntity, private optionLists: OptionListDto[]) {
    super(createFilterKey(field));
    this.field = field;
  }

  private getAlternatives(): Alternative[] {
    if (this.field.optionListId) {
      const optionList = this.optionLists.find(list => list.id === this.field.optionListId);
      return optionList ? getChoiceAlternatives(optionList) ?? [] : [];
    } else {
      return this.field.alternatives ?? [];
    }
  }

  parseValue(labelOrValue: string, makeSticky = false) {
    const alternatives = this.getAlternatives();
    this.alternative = alternatives.find(alt => alt.label === labelOrValue) || alternatives.find(alt => alt.value === labelOrValue);
    const valueToParse = this.alternative && typeof this.alternative.value === 'string' ? this.alternative.value : labelOrValue;
    super.addValue(valueToParse);
  }

  getReadableValue(value: string): string {
    const label = this.getAlternatives().find(alt => alt.value === value)?.label;
    return `${this.alias}: ${label ?? value}`;
  }

  getMultipleValues(): string[] {
    return this.value;
  }

  get filterValues() {
    if (this.field.type === FieldType.Choice && this.value.length > 1) {
      let valuesConcatenatedWithOr = '';
      for (let i = 0; i < this.value.length; i++) {
        const labelOrValue = this.getAlternatives().find(alt => alt.value === this.value[i])?.label ?? this.value[i];
        const prefix = i === 0 ? '' : ' OR ';
        valuesConcatenatedWithOr += `${prefix}${labelOrValue}`;
      }
      return [{ value: `${this.alias}: ${valuesConcatenatedWithOr}`, isSticky: false }];
    }
    return super.filterValues;
  }

  removeValue(index: number | string) {
    if (this.field.type === FieldType.Choice) {
      this.value = [];
    } else {
      super.removeValue(index);
    }
  }
}

export class TreeChoiceFilter extends StringFilter {

  constructor(field: MdfFieldEntity) {
    super(createFilterKey(field));
  }

  parseValue(Value: string, makeSticky = false) {
    this.value.push(Value);
  }

  getReadableValue(value: string): string {
    return `${this.alias}: ${value}`;
  }

  getMultipleValues(): string[] {
    if (this.value.length) {
      return this.value[0].split(TREE_CHOICE_SEPARATOR);
    }
    return [];
  }

  get serializedValue(): string {
    let val = '';
    if (isArrayOfString(this.value)) {
      val = this.value.join(TREE_CHOICE_SEPARATOR);
    }
    return `${this.key}:'${val}'`;
  }

  removeValue(index: number | string) {
    this.value = [];
  }
}

/**
 * Serialized all filters to a string
 */
export const serializeFilters = (filters: Filters, allMdfs: MetaDataSchema[] | null, searchDateOption?: SearchDateOption): string => {
  // For backward compatibility, handle the legacy dateFilter if it exists
  if (filters.dateFilter && (filters.dateFilter.hasValue || searchDateOption)) {
    const isDefaultFilter = isDefaultFilterType(filterTypeForKey(filters.dateFilter.key));
    if (!isDefaultFilter && !filters.customFilters[filters.dateFilter.key]) filters.customFilters[filters.dateFilter.key] = filters.dateFilter;
  }

  if (allMdfs) filters.checkMetadataForm(allMdfs, searchDateOption);

  let serialized = '';
  const allFilters = combineFilters(filters).filter(f => f.shouldSerialize && f.serializeMode === SerializeMode.String && f.hasValue);
  for (const f of allFilters) {
    // Special handling for date filters with searchDateOption (legacy behavior for single dateFilter)
    if (filters.dateFilter && isEqual(f, filters.dateFilter) && checkIfValidDateSearchExceptAnyOrCustom(searchDateOption)) {
      serialized += `${f.key}:${searchDateOption} `;
      continue;
    }
    serialized += `${f.serializedValue} `;
  }

  return serialized.trim();
};

export const checkIfValidDateSearchExceptAnyOrCustom = (searchDateOption?: SearchDateOption) => {
  return searchDateOption === SearchDateOption.PAST_24_HOURS
    || searchDateOption === SearchDateOption.PAST_HOUR
    || searchDateOption === SearchDateOption.PAST_MONTH
    || searchDateOption === SearchDateOption.PAST_WEEK
    || searchDateOption === SearchDateOption.PAST_YEAR
    || searchDateOption === SearchDateOption.PAST
    || searchDateOption === SearchDateOption.FUTURE;
};

/**
 * Folders are a bit special behavior with filters. The current value will always correspond
 * the current navigated folder. However it is either the current, or an ancestor folder that is
 * marked as sticky, as users are allowed to navigate down into folders of a given stickied folder, just not out of it.
 *
 * In addition to that, folder searches are performed using the query parameter 'folderId' in the URL, and not serialized as part
 * of the searchstring as all other filters. This filter therefore only provides query parameters, and not a serialized string.
 *
 * This logic is taken care of in this filter class.
 */
export class FolderIdFilter extends StringFilter {
  constructor() {
    super('folderId', undefined, undefined, undefined, true, undefined, true, true);
    this.serializeMode = SerializeMode.Parameters;
  }

  /**
   * Folder is special - clear value essentially means clear current folder, but if it's stickied
   * we must ensure the current value is reset back to the stickied folder, so the user is essentially
   * redirected back to the original folder if they decided to navigate.
   */
  clearValue() {
    const stickiedFolder = this.getStickiedFolderId();
    if (stickiedFolder !== null) {
      this.value[0] = stickiedFolder;
    } else {
      super.clearValue();
    }
  }

  addValue(value: string) {
    this.setValue([value]);
  }

  parseValue(val: string, makeSticky = false) {
    this.value[0] = val;

    if (makeSticky) {
      /**
       * We avoid direct assignment to ensure Vue reactivity is not affected.
       */
      if (this.stickiedValues.length > 0) {
        this.stickiedValues.splice(0, 1, val);
      } else {
        this.stickiedValues.push(val);
      }
    }
  }

  getStickiedFolderId() {
    return this.stickiedValues.length > 0 ? this.stickiedValues[0] : null;
  }

  /**
   * We never want to show the FolderID value to the user as a filter, since this is made apparent
   * by the visibility of the folder action bar instead.
   */
  get filterValues(): FilterViewValue[] {
    return [];
  }

  get queryParameters(): Record<string, string> {
    return this.value.length === 0 ? {} : { folderId: this.value[0] };
  }
}

class KeldaConfigFilter extends StringFilter {
  private keldaConfigs: KeldaConfigDto[] = [];
  setKeldaConfigs(keldaConfigs: KeldaConfigDto[]) {
    this.keldaConfigs = keldaConfigs;
  }

  getReadableValue() {
    return this.keldaConfigs.find(kc => kc.id === this.value[0])?.name ?? this.value[0];
  }
}

interface ClearFiltersParams {
  filters: Filters;
  serializedOnly?: boolean;
  userDefaultFilters?: ParsedFilter[];
  clearSticky?: boolean;
  keepFolderId?: boolean;
}

/**
 * Clear all filter values. If serialized only is supplied, any filter that does not
 * by default serialized from the searchstring will be skipped. This is useful for filters
 * that are kept in sync via application state, rather than the search string.
 *
 * At the time of writing this, only folders behave like this atm.
 *
 * Will clear the default of their values, and remove all custom filters except the
 * user default ones.
 *
 * For folderId filter we have an option to keep the folderId value if keepFolderId is true.
 */
export const clearFilters = (params: ClearFiltersParams): void => {
  const { filters, serializedOnly, userDefaultFilters, clearSticky = false, keepFolderId = false } = params;

  const skipNonSerialized = typeof serializedOnly === 'boolean' && serializedOnly;

  const defaultFiltersFromUserSettings: Record<string, Filter<any>> = {};
  const customFiltersFromUserSettings: Record<string, Filter<any>> = {};
  for (const parsedFilter of userDefaultFilters ?? []) {
    const { token } = parsedFilter;
    if (token in filters.defaultFilters) {
      defaultFiltersFromUserSettings[token] = filters.defaultFilters[token as keyof DefaultFilters];
    } else if (token in filters.customFilters) {
      customFiltersFromUserSettings[token] = filters.customFilters[token] as Filter<any>;
    }
  }

  filters.customFilters = { ...filters.customFilters, ...customFiltersFromUserSettings };

  const allFilters = combineFilters(filters);
  for (const filter of allFilters) {
    if (!skipNonSerialized && filter.shouldSerialize && !defaultFiltersFromUserSettings[filter.key] && !(keepFolderId && filter.key === 'folderId')) {
      if (clearSticky)
        filter.forceClearAll();
      else
        filter.clearValue();
    }
  }
};

/**
 * Combines all default and custom filters to one array.
 */
export const combineFilters = (filters: Filters): Filter<any>[] => {
  const allFilters = { ...filters.defaultFilters, ...filters.customFilters };
  const values = Object.values(allFilters).filter((filter: Filter<any>) => !!filter);
  return values;
};

export const getExtraInfoFromFilterValue = (val: string): { value: string, extraInfo?: string } => {
  const indexOfPipe = val.lastIndexOf(filterDelimiter);
  if (indexOfPipe > 0) {
    const extraInfo = val.substring(indexOfPipe + 1);
    if (extraInfo.length > 0) {
      return {
        value: val.substring(0, indexOfPipe),
        extraInfo
      };
    }
  }
  return { value: val };
};

/**
 * TODO: Fix typing issue
 * Create a custom filter based on the given {@link MdfFieldEntity}
 */
export const createCustomFilterBasedOnField = (logger: Logger, field: MdfFieldEntity, metadataFormId: string | undefined, dateHelper: DateHelper, layoutSettings: LayoutSettings | null | undefined, users: UserInfoDto[] = [], optionLists: OptionListDto[] = []): Filter<any> => {
  let filter: Filter<any>;
  const filterKey = createFilterKey(field);
  switch (field.type) {
    case FieldType.Date:
      filter = new DateRangeFilter({
        logger,
        key: filterKey,
        dateHelper,
        readableKey: field.fieldId,
      });
      break;
    case FieldType.Number:
      filter = new NumberRangeFilter(logger, filterKey); break;
    case FieldType.Text:
      filter = new StringFilter(filterKey); break;
    case FieldType.Choice:
      filter = new ChoiceFilter(field, optionLists); break;
    case FieldType.MultipleChoice:
      filter = new ChoiceFilter(field, optionLists); break;
    case FieldType.Checkbox:
      filter = new BooleanFilter(filterKey); break;
    case FieldType.User:
      filter = new UserFilter(filterKey, users); break;
    case FieldType.TreeChoice:
      filter = new TreeChoiceFilter(field); break;
    default:
      filter = new StringFilter(filterKey); break;
  }

  if (layoutSettings?.icon) {
    filter.icon = layoutSettings.icon;
  } else if (field.type === FieldType.User) {
    filter.icon = 'person';
  } else {
    filter.icon = 'reorder';
  }

  filter.fieldType = field.type;
  filter.alias = layoutSettings?.label ?? field.fieldId;
  filter.metadataFormId = metadataFormId;
  return filter;
};

/**
 * Finds out whether the given filter is the default form filter.
 */
export const isDefault = (filter: Filter<any>, filters: DefaultFilters): boolean => {
  return filter === filters['filter:formId'] && filter.getValue() === DEFAULT_MDF_ID;
};

export const getDateRange = (option: SearchDateOption): Date[] => {
  const dateArray: Date[] = [];
  const startDate = new Date();
  const endDate = new Date();

  switch (option) {
    case SearchDateOption.ANYTIME:
      return dateArray;
    case SearchDateOption.PAST_HOUR:
      startDate.setHours(startDate.getHours() - 1); break;
    case SearchDateOption.PAST_24_HOURS:
      startDate.setHours(startDate.getHours() - 24); break;
    case SearchDateOption.PAST_WEEK:
      startDate.setDate(startDate.getDate() - 7); break;
    case SearchDateOption.PAST_MONTH:
      startDate.setMonth(startDate.getMonth() - 1); break;
    case SearchDateOption.PAST_YEAR:
      startDate.setFullYear(startDate.getFullYear() - 1); break;
    case SearchDateOption.PAST:
      // From epoch to current date
      dateArray.push(new Date('1970-01-01T00:00:00.000Z'));
      dateArray.push(new Date());
      return dateArray;
    case SearchDateOption.FUTURE:
      // From current date to far future
      dateArray.push(new Date());
      dateArray.push(new Date('2100-01-01T00:00:00.000Z'));
      return dateArray;
    case SearchDateOption.CUSTOM:
      return dateArray;
    default:
      return dateArray;
  }

  dateArray.push(startDate);
  dateArray.push(endDate);
  return dateArray;
};


export const parseArchivedFilterValue = (val: string): ArchivedFilterType | null => {
  if (val === 'yes' || val === 'true') {
    return 'yes';
  } else if (val === 'no' || val === 'false') {
    return 'no';
  } else if (val === 'failed') {
    return 'failed';
  }
  return null;
};

const createFilterEntries = (
  logger: Logger,
  mdf: MetaDataSchema,
  field: MdfFieldEntity,
  dateHelper: DateHelper,
  users: UserInfoDto[],
  optionLists: OptionListDto[]
): Record<string, Filter<any>> => {
  const settings = mdf.getSettingsById(field, ViewType.SEARCH);
  const filterEntries: Record<string, Filter<any>> = {};

  if (!field.sortable && settings && settings.visible === false) {
    // Do nothing - if we find no settings we assume it's visible
  } else if (!field.id.startsWith('default_')) {
    const filter = createCustomFilterBasedOnField(logger, field, mdf.id, dateHelper, settings, users, optionLists);
    filterEntries[field.id] = filter;
    filterEntries[`${mdf.id}_${field.fieldId}`] = filter;
    filterEntries[`${mdf.id}_${createFilterKey(field)}`] = filter;
  }

  return filterEntries;
};

const findSubTypeMdf = (
  field: MdfFieldEntity,
  selectedValue: string,
  customMdfs: MetaDataSchema[]
): MetaDataSchema | undefined => {
  if (field.type !== FieldType.SubType || !field.alternatives) {
    return undefined;
  }

  const selectedAlternative = field.alternatives.find(
    alt => alt.value === selectedValue
  );

  if (!selectedAlternative) return;

  return customMdfs.find(
    mdf => mdf.isSubtype && (mdf.id === selectedAlternative.value || mdf.label === selectedAlternative.label)
  );
};

/**
 * Attempts to match a filter against multiple token variations in the given filter map.
 * Tries the filter token first, then prefixed with each MDF ID.
 */
function matchFilterAgainstTokens(
  parsedFilter: ParsedFilter,
  mdfs: MetaDataSchema[],
  filterMap: Record<string, Filter<any>>
): Filter<any> | undefined {
  const possibleTokens = [
    parsedFilter.token,
    ...mdfs.map(mdf => `${mdf.id}_${parsedFilter.token}`)
  ];

  for (const token of possibleTokens) {
    const filterMatch = filterMap[token];
    if (filterMatch) return filterMatch;
  }

  return undefined;
}

function getPossibleSubtypeFilters(
  logger: Logger,
  dateHelper: DateHelper,
  users: UserInfoDto[],
  optionLists: OptionListDto[],
  customMdfs: MetaDataSchema[],
  selectedSubTypeMdfs: Set<string>,
) {
  const subTypeMdfsToProcess = customMdfs.filter(mdf => mdf.isSubtype && selectedSubTypeMdfs.has(mdf.id));

  // Create filter entries for selected subtypes
  const selectedSubtypeFilters: Record<string, Filter<any>> = {};
  for (const mdf of subTypeMdfsToProcess) {
    if (!mdf.fields) continue;

    for (const field of mdf.fields) {
      const filterEntries = createFilterEntries(logger, mdf, field, dateHelper, users, optionLists);
      Object.assign(selectedSubtypeFilters, filterEntries);
    }
  }

  // Also create filter entries for all subtypes (for fallback)
  const allSubtypeFilters: Record<string, Filter<any>> = {};
  for (const mdf of customMdfs.filter(m => m.isSubtype)) {
    if (!mdf.fields) continue;

    for (const field of mdf.fields) {
      const filterEntries = createFilterEntries(logger, mdf, field, dateHelper, users, optionLists);
      Object.assign(allSubtypeFilters, filterEntries);
    }
  }

  return { subTypeMdfsToProcess, selectedSubtypeFilters, allSubtypeFilters };
}

/**
 * Processes unrecognized filters by trying to match them against subtype fields
 * where the priority is: selected subtypes > all subtypes > all MDFs.
 *
 * This handles the case where multiple MDFs have fields with the same fieldId. When a subtype
 * selection field is present in the search, we prioritize fields from the selected subtype.
 */
function matchSubtypeFilters(
  parsed: ParsedFilter[],
  customMdfs: MetaDataSchema[],
  allMdfs: MetaDataSchema[],
  subTypeMdfsToProcess: MetaDataSchema[],
  selectedSubtypeFilters: Record<string, Filter<any>>,
  allSubtypeFilters: Record<string, Filter<any>>,
  allMetadataFilters: Record<string, Filter<any>>,
): Filter<any>[] {
  const matchedFilters: Filter<any>[] = [];
  for (const possibleFilter of parsed) {
    let filterMatch: Filter<any> | undefined = undefined;

    // Try selected subtype fields first (highest priority)
    filterMatch = matchFilterAgainstTokens(possibleFilter, subTypeMdfsToProcess, selectedSubtypeFilters);

    // Fallback to any subtype field
    if (!filterMatch) {
      const allSubtypeMdfs = customMdfs.filter(m => m.isSubtype);
      filterMatch = matchFilterAgainstTokens(possibleFilter, allSubtypeMdfs, allSubtypeFilters);
    }

    // Final fallback to any MDF
    if (!filterMatch) {
      filterMatch = matchFilterAgainstTokens(possibleFilter, allMdfs, allMetadataFilters);
    }

    if (filterMatch) {
      possibleFilter.isRecognized = true;
      possibleFilter.filterKey = filterMatch.key;
      matchedFilters.push(filterMatch);
    }
  }
  return matchedFilters;
}

/**
 * Creates custom filters for any custom fields that are searchable and visible in the given schemas.
 */
export const matchCustomFilters = (
  logger: Logger,
  customMdfs: MetaDataSchema[],
  defaultMdf: MetaDataSchema | undefined,
  parsedFilters: ParsedFilter[],
  users: UserInfoDto[],
  optionLists: OptionListDto[],
  dateHelper: DateHelper
): Filter<any>[] => {

  const customFilters: Filter<any>[] = [];
  const unrecognizedFilters = parsedFilters.filter((f: ParsedFilter) => !f.isRecognized);
  const possibleCustomFilters: Record<string, Filter<any>> = {};
  const selectedSubTypeMdfs = new Set<string>();

  const customMainTypes = customMdfs.filter(mdf => !mdf.isSubtype);

  // First pass to collect filters for non-subtype fields
  const allMainTypes = [
    ...customMainTypes,
    ...(defaultMdf ? [defaultMdf] : [])
  ];
  for (const mdf of allMainTypes) {
    if (!mdf.fields) continue;
    for (const field of mdf.fields) {
      const filterEntries = createFilterEntries(logger, mdf, field, dateHelper, users, optionLists);
      Object.assign(possibleCustomFilters, filterEntries);
    }
  }

  const allMdfs = [
    ...customMdfs,
    ...(defaultMdf ? [defaultMdf] : []),
  ];
  const allFields = allMdfs.flatMap(mdf => mdf.fields || []);
  const remainingFilters: ParsedFilter[] = [];

  // First pass: match filters from main types and identify selected subtypes
  for (const possibleFilter of unrecognizedFilters) {
    const filterMatch = matchFilterAgainstTokens(possibleFilter, allMainTypes, possibleCustomFilters);

    if (filterMatch) {
      possibleFilter.isRecognized = true;
      possibleFilter.filterKey = filterMatch.key;
      customFilters.push(filterMatch);

      // Check if this is a subtype form selection filter
      const matchingField = allFields.find(field => filterMatch.key === createFilterKey(field));

      if (matchingField && matchingField.type === FieldType.SubType) {
        const subTypeMdf = findSubTypeMdf(matchingField, possibleFilter.value, customMdfs);
        if (subTypeMdf) {
          selectedSubTypeMdfs.add(subTypeMdf.id);
        }
      }
    } else {
      remainingFilters.push(possibleFilter);
    }
  }

  // Second pass to collect filters for fields that are in a subtype
  const { selectedSubtypeFilters, allSubtypeFilters, subTypeMdfsToProcess } = getPossibleSubtypeFilters(
    logger,
    dateHelper,
    users,
    optionLists,
    customMdfs,
    selectedSubTypeMdfs,
  );
  customFilters.push(
    ...matchSubtypeFilters(
      remainingFilters,
      customMdfs,
      allMdfs,
      subTypeMdfsToProcess,
      selectedSubtypeFilters,
      allSubtypeFilters,
      possibleCustomFilters,
    )
  );

  return customFilters;
};


/**
 * Maps the given parsedFilters to the filter state.
 * @param filters The filter class to map the values to.
 * @param parsedFilters The parsed filters containing the values.
 */
export const mapFilters = (logger: Logger, filters: Filters, parsedFilters: ParsedFilter[]) => {

  const combinedFilters = combineFilters(filters).filter(f => f.hasValue);

  // Reset each filter that has a value first since we want to map exactly against the parsed filters.
  // This is necessary to also clear filters that may have multiple values.
  for (const filter of combinedFilters) {
    filter.clearValue();
  }

  for (const parsedFilter of parsedFilters) {
    const key: string = parsedFilter.token;
    const value: string = parsedFilter.value;
    const type = filterTypeForKey(key);
    const filter: Filter<any> | undefined = filters.defaultFilters[type as keyof DefaultFilters]
      || filters.customFilters[key]
      || (filters.dateFilter.readableKey === key ? filters.dateFilter : undefined);
    if (filter) {
      filter.parseValue(value, parsedFilter.isSticky);
    } else if (parsedFilter.filterKey) {
      const customFilter = filters.customFilters[parsedFilter.filterKey];
      if (customFilter) {
        customFilter.parseValue(value, parsedFilter.isSticky);
      } else {
        logger.warn(`Recognized value found, but no matching filter ${JSON.stringify(parsedFilter)}`);
      }
    } else {
      logger.warn(`Recognized value found, but no matching filter ${JSON.stringify(parsedFilter)}`);
    }
  }
};


/**
 * Collect all filter query parameters from the default filters.
 */
export const getFilterQueryParams = (filters: Filters) => {

  let filterParams: Record<string, string> = {};
  for (const f in filters.defaultFilters) {
    const filter = filters.defaultFilters[f as keyof DefaultFilters];
    if (filter.serializeMode === SerializeMode.Parameters) {
      filterParams = Object.assign(filterParams, filter.queryParameters);
    }
  }
  return filterParams;
};


/**
 * Extract a ParsedFilter object from a matched token string, and perform some validation.
 *
 * @param token the matched token:value string
 *
 * @param fieldIdExists Record<string, boolean> a map of all field's fieldId
 * This is used to check if the token is a fieldId or not, if it is a fieldId, we will add a prefix to the token
 */
function extractFilter(token: string, recognizedFilters?: Record<string, Filter<any> | undefined>): ParsedFilter {
  const [tokenName, ...spreadTokenValue] = split(token, ':');

  let tokenValue = spreadTokenValue.join(':');

  const staticTokenKey = tryMatchStaticFilterKey(tokenName.trim());

  const isExactSearch = isQuotedText(tokenValue.trim());
  const extraInfoParse = getExtraInfoFromFilterValue(tokenValue);
  const isSticky = extraInfoParse?.extraInfo === extraInfoSticky;
  tokenValue = isSticky && extraInfoParse.value || tokenValue;
  const sanitizedTokenValue = tokenValue.replace(/['"]/g, '').trim();

  // For a recognized token, validate the value where relevant.
  let isRecognized = staticTokenKey !== null || !!(recognizedFilters && tokenName in recognizedFilters);
  if (isRecognized) {
    isRecognized = checkValueValidity(tokenName as StaticFilterKeywords, tokenValue);
  }

  return {
    isExactSearch,
    token: staticTokenKey ?? tokenName,
    value: sanitizedTokenValue,
    isRecognized,
    isSticky,
  };
}

function isPartialGuid(value: string) {
  const partialGuidRegex = /^([0-9a-fA-F]{1,8})(-[0-9a-fA-F]{1,4}){0,3}([-]?[0-9a-fA-F]{1,12})?$/;
  return partialGuidRegex.test(value);
}


/**
 * Check whether the value given for a filter is a valid value. This is especially useful for filters that
 * have predefined values such as ItemType.
 *
 * @param key filter key to check against
 * @param value The value for the key
 */
function checkValueValidity(key: StaticFilterKeywords, value: string): boolean {
  switch (key) {
    case StaticFilterKeywords.ItemType:
      if (!hasSearchCategoryOnly(value)) {
        return false;
      }
      break;
    case StaticFilterKeywords.FolderId:
    case StaticFilterKeywords.ItemId:
      return isValidGUID(value) || isPartialGuid(value);
  }

  return true;
}


/**
 * Will separate all token:value strings that match {@link buildFilterRegex}
 * @param originalSearchString The original raw search string
 */
export function separateFiltersFromSearchString(originalSearchString: string, recognizedFilters?: Record<string, Filter<any> | undefined>): SeparatedFilters {
  if (isEmpty(originalSearchString)) return { remainingSearchString: originalSearchString, parsedFilters: [] };

  const possibleFilters = getFiltersFromSearchString(originalSearchString);

  if (possibleFilters === null)
    return { remainingSearchString: originalSearchString, parsedFilters: [] };

  const searchFilters = isEmpty(possibleFilters) ? [] : possibleFilters.map(token => extractFilter(token, recognizedFilters));
  const remainingSearchString = removeFiltersFromString(possibleFilters, originalSearchString);

  return { remainingSearchString, parsedFilters: searchFilters };
}


/**
 * Checks if the input string is a prefix a of a single StaticFilterKeywords,
 * and if so returns that key.
 */
export function tryMatchStaticFilterKey(filterKey: string): StaticFilterKeywords | null {
  let matches: StaticFilterKeywords[] = [];

  const validFilters = Object.values(StaticFilterKeywords);
  for (const name of validFilters) {
    if (startsWith(name.toLowerCase(), filterKey.toLowerCase())) {
      matches.push(name);
    }
  }

  // We may have more than one match. Let's find out if there's an exact match, and use that.
  // If we are unable to, next block will bail out as it did before because we cannot reliably
  // know which filter the user intended to use. For example f:value could match three different filters.
  // However folder:value is valid, but the above block also matched folderId:value. If the key matches
  // one of the filters exactly, we use that.
  if (matches.length > 1) {
    for (const match of matches) {
      if (match.toLowerCase() === filterKey.toLowerCase()) {
        matches = [match];
        break;
      }
    }
  }

  return matches.length === 1 ? matches[0] : null;
}


/**
 * Given an array of filters that were matched, set them on the filters object.
 *
 * Vue 2's reactivity model does not allow for a direct assignment on an object, it will not be tracked.
 * To still be able to unit test this, pass in a VueSet function from the UI to have it do this in a reactivity friendly way.
 *
 * @param filters The filters object containing all filter state.
 * @param matchedFilters The filters that have been matched, typically a result of the function matchCustomFilters
 * @param set Optionally provide a function to use a different assignment method.
 */
export const setCustomFiltersToState = (filters: Filters, matchedFilters: Filter<any>[], set?: VueSet) => {
  // Make sure the UI reacts to the changes properly using Vue.set
  for (const filter of matchedFilters) {
    // For backward compatibility, check if this is the legacy dateFilter
    // But now we allow multiple date filters, so we don't skip existing ones
    if (filters.dateFilter && filter.key === filters.dateFilter.key) {
      // Still update the legacy dateFilter reference for backward compatibility
      if (set) {
        set({
          object: filters.customFilters,
          key: filters.dateFilter.key,
          value: filters.dateFilter
        });
      } else {
        filters.customFilters[filters.dateFilter.key] = filters.dateFilter;
      }
    } else if (set) {
      set({
        object: filters.customFilters,
        key: filter.key,
        value: filter
      });
    } else {
      filters.customFilters[filter.key] = filter;
    }
  }
};


/**
 * Responsible for pre-processing any values that are detected in the search string that have a
 * serialization mode set to Parameters. We strip these away from the search string and parse them beforehand,
 * this way we are able to use these as query parameters without polluting the search string with the actual
 * filter value.
 *
 * This function also takes care of removing single values if we detect the filter already has a stickied value for single
 * value only filters. This prevents users from overriding the value via text based search.
 *
 * @param filters The current filters object - if a parameter based value is found that matches a filter class, it will be parsed into app state there
 * @param separatedFilters The ready parsed search string
 * @returns The remaining search string, with any parameterized key:value removed, if any, null if no parameterized key:value was found
 */
export function preProcessSearchString(filters: Filters, searchString: string): string | null {

  const separatedFilters = separateFiltersFromSearchString(searchString);
  const parsedFiltersToKeep = separatedFilters.parsedFilters.filter(p => !p.isRecognized);
  let changesToBeMade = false;
  for (const f of separatedFilters.parsedFilters.filter(p => p.isRecognized)) {
    const type = filterTypeForKey(f.token);
    invariant(isDefaultFilterType(type), 'Expecting default filter key');
    const filter = filters.defaultFilters[type];
    if (filter.serializeMode === SerializeMode.String) {
      if (filter.singleValue && filter.hasSticky) {
        // We cannot allow the user to set a different value for it.
        // We will simply skip it here, so it doesn't get serialized back again.
        changesToBeMade = true;
      } else {
        parsedFiltersToKeep.push(f);
      }
    } else {
      changesToBeMade = true;
      filter.parseValue(f.value, f.isSticky);
    }
  }

  if (!changesToBeMade) {
    return null;
  }

  const stringifiedParsedFilters = parsedFiltersToKeep.map(p => stringifyParsedFilter(p)).join(' ').trim();
  return `${stringifiedParsedFilters} ${separatedFilters.remainingSearchString}`.trim();
}


export function stringifyParsedFilter(parsedFilter: ParsedFilter) {
  const isSticky = parsedFilter.isSticky ? `${filterDelimiter}${extraInfoSticky}` : '';
  return parsedFilter.isExactSearch ? `${parsedFilter.token}:"${parsedFilter.value}"${isSticky}` : `${parsedFilter.token}:${parsedFilter.value}${isSticky}`;
}


/**
 * Get array of parsed filters from Filter objects
 */
export const getParsedFilters = (filters: Filter<any>[]) => {
  const parsedFilters: ParsedFilter[] = [];
  for (const filter of filters) {
    filter.filterValues?.forEach(val => parsedFilters.push({
      token: filter.key,
      value: val.value,
      isExactSearch: isQuotedText(val.value),
      isRecognized: true,
      isSticky: filter.isSticky(val)
    }));
  }
  return parsedFilters;
};

export const createFilterKey = (field: MdfFieldEntity) => {
  return METADATA_FIELD_FILTER_PREFIX + field.id;
};
